import PageHeader from '../components/ui/PageHeader';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArticleService } from '../services/articleService';
import { ScheduleService } from '../services/scheduleService';
import { PlatformService } from '../services/platformService';
import { formatScheduledAtWithWeekday } from '../utils/dateFormatter';
import { getScheduledAtTime, parseScheduledAt } from '../utils/dateTimeUtils';
import { Article, Schedule, Platform } from '../types';
import { Button } from '../components/ui/Button';
import { SkeletonStatCard, SkeletonArticleCard } from '../components/ui/Skeleton';
import { UpdatesBox } from '../components/UpdatesBox';
import ArticleCard from '../components/ArticleCard';
import useArticleLiveUpdates from '../hooks/useArticleLiveUpdates';
import useAuthorNames from '../hooks/useAuthorNames';
import { 
  DocumentTextIcon, 
  CalendarIcon
} from '@heroicons/react/24/outline';

export const DashboardPage: React.FC = () => {
  const { user, hasRole } = useAuth();
  const [articles, setArticles] = useState<Article[]>([]);
  useArticleLiveUpdates(user, setArticles);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const authorNames = useAuthorNames(articles, {
    enabled: articles.length > 0,
    onError: (error) => {
      console.error('Fehler beim Batch-Laden der Autoren:', error);
    }
  });
  
  // Progressive Loading States
  const [isLoadingArticles, setIsLoadingArticles] = useState(true);
  const [isLoadingSchedules, setIsLoadingSchedules] = useState(true);

  // Artikel laden (unabhängig)
  useEffect(() => {
    const loadArticles = async () => {
      if (!user) return;
      
      setIsLoadingArticles(true);
      try {
        let userArticles: Article[];
        if (hasRole(['administrator', 'redakteur'])) {
          userArticles = await ArticleService.getAllArticles(user, 5);
        } else {
          userArticles = await ArticleService.getUserArticles(user.id, user, 5);
        }
        setArticles(userArticles);
        
      } catch (error) {
        console.error('Error loading articles:', error);
      } finally {
        setIsLoadingArticles(false);
      }
    };

    loadArticles();
  }, [user, hasRole]);

  // Schedules und Plattformen laden (unabhängig, parallel)
  useEffect(() => {
    const loadSchedulesAndPlatforms = async () => {
      if (!user || !hasRole(['autor', 'administrator'])) {
        setIsLoadingSchedules(false);
        return;
      }
      
      setIsLoadingSchedules(true);
      try {
        const [userSchedules, allPlatforms] = await Promise.all([
          ScheduleService.getSchedulesByAuthor(user.id),
          PlatformService.getAllPlatforms()
        ]);
        setSchedules(userSchedules);
        setPlatforms(allPlatforms);
      } catch (error) {
        console.error('Error loading schedules and platforms:', error);
      } finally {
        setIsLoadingSchedules(false);
      }
    };

    loadSchedulesAndPlatforms();
  }, [user, hasRole]);

  // Status-Funktionen werden jetzt aus der zentralen Utility importiert

  const recentArticles = articles.slice(0, 5);

  // Filtere geplante Artikel (zukünftige Veröffentlichungen) nur für den eingeloggten Autor
  const plannedArticles = articles
    .filter(article => {
      if (article.authorId !== user!.id || !article.scheduledAt) return false;
      const scheduledDate = parseScheduledAt(article.scheduledAt);
      return !!scheduledDate &&
        scheduledDate > new Date() &&
        (article.status === 'scheduled' || article.status === 'ready');
    })
    .sort((a, b) => (getScheduledAtTime(a.scheduledAt) ?? 0) - (getScheduledAtTime(b.scheduledAt) ?? 0))
    .slice(0, 3); // Maximal 3 geplante Artikel anzeigen

  return (
    <>
    <PageHeader title="Dashboard" description={`Willkommen${user?.name ? ", " + user.name : ""}. Hier findest du deine Artikel und anstehenden Termine.`} />
    <div className="flex gap-6">
      {/* Sticky Sidebar - e-Redaktion Anleitung */}
      <div className="hidden lg:block lg:w-80 flex-shrink-0">
        <div className="sticky top-24">
          <div className="card">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </div>
              <div className="ml-3">
                <h2 className="text-lg font-medium text-gray-900">e-Redaktion Anleitung</h2>
                <p className="text-sm text-gray-500">Erfahre, wie das KI-gestützte Lektorat funktioniert</p>
              </div>
            </div>
            
            <a
              href="https://www.youtube.com/watch?v=EIMKDzTBD50"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Video ansehen
            </a>
          </div>
        </div>
      </div>

      {/* Hauptinhalt */}
      <div className="flex-1 min-w-0 space-y-6">
        {/* Updates Box */}
        <UpdatesBox />
        
        {/* Statistiken */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Feste Termine (für Autoren und Administratoren) */}
        {hasRole(['autor', 'administrator']) ? (
          isLoadingSchedules ? (
            <SkeletonStatCard />
          ) : (
            <div className="card">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CalendarIcon className="w-8 h-8 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Feste Termine</p>
                  <p className="text-2xl font-semibold text-gray-900">{schedules.length}</p>
                </div>
              </div>
              {schedules.length > 0 && (
                <div className="mt-3 space-y-2">
                  {schedules
                    .map((schedule) => {
                      const platform = platforms.find(p => p.id === schedule.platformId);
                      return { schedule, platform };
                    })
                    .sort((a, b) => {
                      const nameA = a.platform?.name || 'Unbekannte Plattform';
                      const nameB = b.platform?.name || 'Unbekannte Plattform';
                      return nameA.localeCompare(nameB, 'de-DE');
                    })
                    .slice(0, 2)
                    .map(({ schedule, platform }) => (
                      <div
                        key={schedule.id}
                        className="flex items-center p-2 border border-gray-200 rounded-lg bg-blue-50"
                      >
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                              <CalendarIcon className="w-3 h-3 text-blue-600" />
                            </div>
                          </div>
                          <div className="ml-2">
                            <p className="text-xs font-medium text-gray-900">
                              {platform?.name || 'Unbekannte Plattform'}
                            </p>
                            <p className="text-xs text-gray-600">
                              {ScheduleService.getDayName(schedule.dayOfWeek)} um {schedule.time}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )
        ) : (
          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CalendarIcon className="w-8 h-8 text-gray-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Feste Termine</p>
                <p className="text-2xl font-semibold text-gray-900">-</p>
              </div>
            </div>
          </div>
        )}

        {/* Geplante Artikel (für alle Benutzer) */}
        {isLoadingArticles ? (
          <SkeletonStatCard />
        ) : (
          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CalendarIcon className="w-8 h-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Geplante Artikel</p>
                <p className="text-2xl font-semibold text-gray-900">{plannedArticles.length}</p>
              </div>
            </div>
            {plannedArticles.length > 0 && (
              <div className="mt-3 space-y-2">
                {plannedArticles.map((article) => (
                  <div
                    key={article.id}
                    className="flex items-center p-2 border border-gray-200 rounded-lg bg-green-50"
                  >
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                          <DocumentTextIcon className="w-3 h-3 text-green-600" />
                        </div>
                      </div>
                      <div className="ml-2">
                        <p className="text-xs font-medium text-gray-900 line-clamp-1">
                          {article.title || 'Ohne Titel'}
                        </p>
                        <p className="text-xs text-gray-600">
                          {formatScheduledAtWithWeekday(article.scheduledAt!)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      

      {/* Letzte Artikel */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-900">Letzte Artikel</h2>
          <Link to="/articles" className="text-sm text-blue-600 hover:text-blue-500">
            Alle anzeigen
          </Link>
        </div>

        {isLoadingArticles ? (
          <div className="space-y-4">
            <SkeletonArticleCard />
            <SkeletonArticleCard />
            <SkeletonArticleCard />
          </div>
        ) : recentArticles.length === 0 ? (
          <div className="text-center py-8">
            <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Noch keine Artikel vorhanden</p>
            <Link to="/wizard">
              <Button variant="outline" className="mt-4">
                Ersten Artikel erstellen
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {recentArticles.map((article) => (
              <ArticleCard
                key={article.id}
                article={article}
                variant="dashboard"
                authorName={authorNames[article.authorId]}
                formatScheduled={formatScheduledAtWithWeekday}
              />
            ))}
          </div>
        )}
      </div>
      </div> {/* Ende Hauptinhalt */}
    </div>
    </>
  );
};
